import { ChangeEvent, useEffect, useRef, useState } from "react";
import { NavigateOptions, useNavigate } from "react-router";
import { getActionUrl } from "../../app/draftHelper";
import { UserRole } from "../../app/userSlice";
import { useAppSelector } from "../../app/hooks";
import { SimpleOption } from "../../components/input/SimpleListbox";
import Avatar from "../../components/Avatar";
import NoDataTableBody from "../../components/NoDataTableBody";
import SimplePagination from "../../components/SimplePagination";
import Select from "../../components/input/Select";
import { getNotificationOptions, getNotificationUrl } from "./notificationUtil";
import {
	NotificationBaseViewModel,
	NotificationDataTableQueryModel,
	NotificationTypes,
	useDeleteNotificationsMutation,
	useLazyDraftActionQuery,
	useNotificationsQuery,
	useSetNotificationsAsViewedMutation,
} from "./notificationsApiSlice";

export default function Notifications() {
	const { role } = useAppSelector((state) => state.user);
	const _displayLength = 20;
	const initQueryargs: NotificationDataTableQueryModel = {
		typeFilter: "All",
		iDisplayStart: 0,
		iDisplayLength: _displayLength,
		iSortCol_0: 0,
		sSortDir_0: "desc",
	};

	const [notificationOptions, setNotificationOptions] = useState<SimpleOption[]>([]);
	const [notificationFilter, setNotificationFilter] = useState("All");
	const [page, setPage] = useState(0);
	const [queryargs, setQueryargs] = useState(initQueryargs);
	const [allCheckboxIds, setAllCheckboxIds] = useState<number[]>([]);
	const [checkedIds, setCheckedIds] = useState<number[]>([]);
	const [allChecked, setAllChecked] = useState(false);
	const [notificationId, setNotificationId] = useState<number>();
	const [notificationUrl, setNotificationUrl] = useState<string>();
	const [notificationUrlOptions, setNotificationUrlOptions] = useState<NavigateOptions>();

	const [callGetDraftAction] = useLazyDraftActionQuery();
	const [callSetAsViewed] = useSetNotificationsAsViewedMutation();
	const [callDeleteNotifications] = useDeleteNotificationsMutation();

	const { data, error } = useNotificationsQuery(queryargs);

	const navigate = useNavigate();
	const ref = useRef<Array<HTMLInputElement | null>>([]);

	useEffect(() => {
		setQueryargs({
			...queryargs,
			iDisplayStart: _displayLength * page,
			typeFilter: notificationFilter,
		});
		setAllCheckboxIds([]);
		setCheckedIds([]);
	}, [page, notificationFilter]);

	useEffect(() => {
		if (checkedIds.length === allCheckboxIds.length && allCheckboxIds.length != 0) {
			setAllChecked(true);
		} else {
			setAllChecked(false);
		}
	}, [checkedIds]);

	useEffect(() => {
		const options: SimpleOption[] = [{ value: "All", label: "All" }];

		if (data) {
			const notificationtypes = JSON.parse(data.notificationTypes);
			for (let key in notificationtypes) {
				const value = notificationtypes[key];
				options.push({ value: key, label: value });
			}
			setNotificationOptions(options);
		}

		const _temp: number[] = [];

		data?.aaData.map((row) => {
			if (row.canBeDeleted) {
				_temp.push(row.id);
			}
		});
		setAllCheckboxIds(_temp);
	}, [data]);

	useEffect(() => {
		if (notificationUrl) {
			if (notificationId) handleMarkAsRead(notificationId);
			navigate(notificationUrl, notificationUrlOptions);
			setNotificationUrl(undefined);
		}
	}, [notificationUrl]);

	function handleFilterChange(value: string): void {
		setNotificationFilter(value);
	}

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	function handleMarkAllAsRead() {
		callSetAsViewed(checkedIds)
			.unwrap()
			.then(() => {
				setCheckedIds([]);
				changeCheckboxFill(false);
			})
			.catch((error) => {
				console.log(error);
			});
	}

	function handleMarkAsRead(rowId: number) {
		const singleId: number[] = [rowId];
		callSetAsViewed(singleId);
	}

	function handleDelete() {
		callDeleteNotifications(checkedIds)
			.unwrap()
			.then(() => {
				setCheckedIds([]);
			})
			.catch((error) => {
				console.log(error);
			});
	}

	function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			setCheckedIds([...checkedIds, Number(e.target.value)]);
		} else {
			setCheckedIds(checkedIds.filter((item) => item !== Number(e.target.value)));
		}
	}

	function handleSelectAll(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			setCheckedIds(allCheckboxIds);
			changeCheckboxFill(true);
		} else {
			setCheckedIds([]);
			changeCheckboxFill(false);
		}
	}

	function changeCheckboxFill(checked: boolean) {
		for (let i = 0; i < ref.current.length; i++) {
			if (ref.current[i] !== undefined && ref.current[i] !== null) {
				ref.current[i]!.checked = checked;
			}
		}
	}

	function handleNotificationClick(event: any, notification: NotificationBaseViewModel) {
		setNotificationId(notification.id);

		if (role === UserRole.Writer) {
			if (notification.notificationType == NotificationTypes.NewMessage) {
				getDraftInfo(notification.lastDraftId);
				setNotificationUrlOptions(getNotificationOptions(notification));
			} else if (notification.notificationType == NotificationTypes.DraftComment) {
				getDraftInfo(notification.draftId);
			} else {
				setNotificationUrl(getNotificationUrl(notification));
				setNotificationUrlOptions(getNotificationOptions(notification));
			}
		} else {
			setNotificationUrl(getNotificationUrl(notification));
			setNotificationUrlOptions(getNotificationOptions(notification));
		}
	}

	function getDraftInfo(draftId: number | undefined) {
		if (draftId) {
			callGetDraftAction(draftId)
				.unwrap()
				.then((data) => {
					if (data.action) {
						const actionUrl = getActionUrl(data.action, draftId);
						setNotificationUrl(actionUrl ?? undefined);
					}
				})
				.catch((error) => {
					console.log(error);
				});
		}
	}

	return (
		<div>
			<h1>Notifications</h1>
			<div className="mt-4 flex justify-between items-end">
				<Select
					value={notificationFilter}
					setValue={(v) => handleFilterChange(v as string)}
					label="Type"
					simpleListBoxId="notificationFilter"
					options={notificationOptions}
					simpleListBoxClasses="w-64"
				/>
				<div>
					<button className="btn btn-primary mr-2" onClick={() => handleMarkAllAsRead()}>
						Mark As Read
					</button>
					<button
						className="btn btn-primary mr-2"
						disabled={checkedIds.length == 0}
						onClick={() => handleDelete()}>
						Delete
					</button>
				</div>
			</div>
			<div className="shadow-sm overflow-hidden mt-4">
				<table className="tbl tbl-data">
					<thead>
						<tr>
							<th>
								<input
									id="selectAllCheckbox"
									type="checkbox"
									className="h-4 w-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
									onChange={(e) => handleSelectAll(e)}
									checked={allChecked}
								/>
							</th>
							<th>Date</th>
							<th>Type</th>
							<th></th>
							<th>Notification</th>
							<th>Read</th>
						</tr>
					</thead>

					{data?.aaData.length == 0 ? (
						<NoDataTableBody
							columnCount={6}
							message="You have no notifications at this time."
						/>
					) : (
						<tbody>
							{data?.aaData.map((row, index) => {
								return (
									<tr
										key={"notification-" + row.id}
										className={row.viewed ? "" : "!bg-green-50"}
										id={row.viewed ? undefined : "unread-notification"}>
										<td>
											{row.canBeDeleted ? (
												<input
													id={"notification-" + row.id}
													value={row.id}
													ref={(element) => {
														ref.current[index] = element;
													}}
													type="checkbox"
													className="h-4 w-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
													onChange={(e) => handleCheckboxChange(e)}
												/>
											) : null}
										</td>
										<td>{row.receivedDate}</td>
										<td>{row.notificationType}</td>
										<td className="avatar-cell">
											{!row.forceShowWithoutInitials ? (
												<Avatar
													hasImage={row.avatarUserHasImage}
													initials={row.avatarUserInitials}
													userId={row.avatarUserId}
												/>
											) : null}
										</td>
										<td
											dangerouslySetInnerHTML={{
												__html: row.body,
											}}
											className="hover:cursor-pointer"
											onClick={(e) => handleNotificationClick(e, row)}></td>
										<td className="!text-center">
											{!row.viewed && row.canBeSetAsViewed && (
												<span
													className="notification-icon h-3 w-3 inline-block bg-slate-300 rounded-full hover:h-4 hover:w-4 hover:cursor-pointer"
													onClick={() => handleMarkAsRead(row.id)}></span>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					)}

					{(data?.iTotalRecords ?? 0) > queryargs.iDisplayLength && (
						<tfoot>
							<tr>
								<td colSpan={7}>
									<SimplePagination
										itemCount={data?.iTotalRecords}
										page={page}
										pageSize={queryargs.iDisplayLength}
										onChange={(page) => handlePageChange(page)}
									/>
								</td>
							</tr>
						</tfoot>
					)}
				</table>
			</div>
		</div>
	);
}
